import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  QueryList,
  signal,
  ViewChildren,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AlertController, IonicSafeString } from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IonicScrollManagerService } from '@okcargo-apps/layout-ionic';
import { EMessageLevel, MessagesUiService, MessageUiIonicComponent } from '@okcargo-apps/messages-ui';
import { IFreightDetailedForShipper, UUID, UUID_NULL } from '@okcargo-apps/shared-domain';
import { filter, Subscription } from 'rxjs';
import { FreightShipperEditGeneralFormComponent } from '../freight-shipper-edit-general-form/freight-shipper-edit-general-form.component';
import { FreightShipperEditStopFormComponent } from '../freight-shipper-edit-stop-form/freight-shipper-edit-stop-form.component';
import { FreightShipperFormsTriggersService } from '../freight-shipper-forms-triggers.service';
import { FreightShipperFormsService } from '../freight-shipper-forms.service';
import { FreightShipperService } from '../freight-shipper.service';

@Component({
  selector: 'lib-freight-shipper-edit-main-form',
  templateUrl: './freight-shipper-edit-main-form.component.html',
  styleUrls: ['./freight-shipper-edit-main-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    TranslateModule,
    MessageUiIonicComponent,
    FreightShipperEditGeneralFormComponent,
    FreightShipperEditStopFormComponent,
  ],
})
export class FreightShipperEditMainFormComponent implements OnInit, OnDestroy {
  public freightId = input.required<UUID>();

  @ViewChildren('generalForm') generalForm!: QueryList<FreightShipperEditGeneralFormComponent>;
  @ViewChildren('stopForm') stopForms!: QueryList<FreightShipperEditStopFormComponent>;

  private readonly freightShipService = inject(FreightShipperService);
  private readonly freightShipFormsService = inject(FreightShipperFormsService);
  private readonly freightShipFormsTriggersService = inject(FreightShipperFormsTriggersService);
  private readonly ionicScrollManagerService = inject(IonicScrollManagerService);
  private readonly translate = inject(TranslateService);
  private readonly messagesUiService = inject(MessagesUiService);
  private readonly router = inject(Router);
  private readonly alertController = inject(AlertController);

  public messages = computed(() => this.messagesUiService.getMessagesForObjectId(this.freightId()));

  public freight = this.freightShipFormsService.freight;
  public stops = computed(() => this.freight().stopsDetailed);

  // navigation control: to clear/load data when user lands (from happy path, from history,...)
  private readonly pathFragments = ['ship-edit', 'ship-new'];
  private readonly _navigationOccurred = signal(false);
  private routerSubscription: Subscription | undefined;

  // transitions / visibility
  hiddenStops = false;
  hiddenStopId: UUID | null = null;

  constructor() {
    /*
    Si detectamos la navegación, entonces cargamos datos. (ver comentario en ngOnInit)

    En el método loadFreightDetail() primero se limpian los datos del servicio y después,
    si hay id que no sea UUID_NULL (valor por defecto para identificar los freight nuevos), se cargan datos.
    Si el id es UUID_NULL, comprobar si hay en el historial un freight sin guardar y, si lo hay, preguntar al usuario
    si lo quiere recuperar.

    En la limpieza del servicio hay que tener en cuenta que no se pueden modificar varias veces los signals,
    solo se debe hacer un set por cada signal, para evitar disparos innecesarios de los effect().
    Por tanto, la inserción de las paradas por defecto se tiene que hacer en un único set, proporcionando el array
    completo de paradas. Además, la creación de cada una de esas paradas no debe incluir acceder a la información del
    mismo signal que se va a actualizar (en este caso, en el servicio, _stops()) ya que eso provocaba un bucle infinito
    porque desencadenaba otra vez cambios en el signal, lo que hacía que entrara en el effect, lo que provocaba cambios
    en el signal,...

    Con la limpieza y carga de datos solo cuando se confirma que hay navegación nos aseguramos de que se carga
    la información correcta también cuando no se ejecuta el onInit debido a que la instancia existía
    (p.ej. se llega desde el historial de navegación o Angular/Ionic no había destruido el componente y se
    vuelve a navegar hasta él).

    Importante: en todos los effect para efectos secundarios controlados por signals booleanos hacemos inmediatamente
    un set(false) si es true para evitar bucles infinitos.
    */
    effect(() => {
      if (this._navigationOccurred()) {
        this._navigationOccurred.set(false);
        this.freightShipFormsService.resetAll();
        this.checkFreightInStore();
      }
    });

    effect(() => {
      if (this.freightShipFormsTriggersService.isFreightShipperSaveTrigger()) {
        this.freightShipFormsTriggersService.resetFreightShipperSaveTrigger();
        this.updateOrCreateFreight();
      }
    });

    effect(() => {
      if (this.freightShipFormsTriggersService.isAddStopTrigger()) {
        this.freightShipFormsTriggersService.resetAddStopTrigger();
        this.addStop();
      }
    });
  }

  ngOnInit(): void {
    /*
    Estos signal ya deberían ser false porque se resetean en los effect si son true, pero "if by the flies"
     */
    if (this.freightShipFormsTriggersService.isFreightShipperSaveTrigger()) {
      this.freightShipFormsTriggersService.resetFreightShipperSaveTrigger();
    }

    if (this.freightShipFormsTriggersService.isAddStopTrigger()) {
      this.freightShipFormsTriggersService.resetAddStopTrigger();
    }

    /*
    La llamada a la carga de datos no se hace en el onInit, sino en el effect() que controla si hay evento
    de navegación finalizado y coincide con una de las páginas en las que se carga el componente.

    Nos suscribimos a los eventos de navegación y, con un signal booleano en el componente, detectaremos si
    venimos de una navegación o no.

    Importante: en el effect para ese signal hacemos inmediatamente un set(false) si es true para evitar bucles
    infinitos.

    (ver comentario en constructor)
     */

    // navigation events
    this.routerSubscription = this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        filter((event: NavigationEnd) => this.pathFragments.some((fragment) => event.url.includes(fragment))),
      )
      .subscribe(() => {
        this._navigationOccurred.set(true);
      });
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  public addStop(): void {
    const newStopId = this.freightShipFormsService.addStop();
    this.moveScroll(newStopId, 50);
  }

  public onRemoveStop(stopId: UUID): void {
    // transition
    this.hiddenStopId = stopId;

    const currentIndex = this.stops().findIndex((stop) => stop.id === stopId);

    const stopIdToScroll = currentIndex > 0 ? this.stops()[currentIndex - 1].id : this.stops()[1].id;

    this.syncAllStopForms();
    this.freightShipFormsService.removeStop(stopId);

    setTimeout(() => {
      this.hiddenStopId = null;
      this.moveScroll(stopIdToScroll, 50);
    }, 200);
  }

  public onReorderStop(moveEvent: { stopId: UUID; newPosition: number }): void {
    // transition
    this.hiddenStops = true;

    this.syncAllStopForms();
    this.freightShipFormsService.reorderStops(moveEvent.stopId, moveEvent.newPosition);

    setTimeout(() => {
      this.hiddenStops = false;
      this.moveScroll(moveEvent.stopId, 100);
    }, 300);
  }

  public onStopFormUpdated(stopId: UUID): void {
    const stopFormComponent = this.stopForms.find((form) => form.currentStop().id === stopId);

    if (stopFormComponent) {
      const updatedData = stopFormComponent.formData;
      this.freightShipFormsService.updateStop(stopId, updatedData);
    }
  }

  public onGeneralFormUpdated(freight: IFreightDetailedForShipper): void {
    this.freightShipFormsService.updateGeneralData(freight);
  }

  public async updateOrCreateFreight(): Promise<void> {
    this.freightShipFormsTriggersService.resetFreightShipperSaveTrigger();

    this.messagesUiService.removeMessageByCriteria({
      objectId: this.freightId(),
    });

    const errors = this.validateAllForms();

    if (errors.length > 0 || this.freightShipFormsService.hasValidationErrors()) {
      this.messagesUiService.setMessage({
        objectId: this.freightId(),
        message: ['Hay errores que debe corregir: ' + errors.join(', ')],
        level: EMessageLevel.ERROR,
      });
      this.moveScroll('messagesTopFreightFormComponent', 50);
      return;
    }

    try {
      const result = await this.freightShipService.updateOrCreateFreight(this.freight());

      if (!result.saved) {
        this.messagesUiService.setMessage({
          objectId: this.freightId(),
          message: ['No se han detectado cambios que guardar.'],
          level: EMessageLevel.INFO,
        });
        this.moveScroll('messagesTopFreightFormComponent', 50);
        return;
      }

      let successMessage = '';

      if (result.isNew) {
        successMessage = `Ha subido a nuestro sistema una nueva carga ("${this.freight().reference}"). Estamos procesando la información. En breve recibirá una oferta.`;
      } else {
        const partsUpdated: string[] = [];
        if (result.generalDataUpdated) {
          partsUpdated.push('datos generales');
        }
        if (result.updatedStopIndexes.length > 0) {
          partsUpdated.push(
            `${result.updatedStopIndexes.length} parada${result.updatedStopIndexes.length !== 1 ? 's' : ''}`,
          );
        }
        successMessage = `Ha actualizado los siguientes elementos de la carga "${this.freight().reference}": ${partsUpdated.join(' y ')}. En breve recibirá su nuevo precio.`;
      }

      this.messagesUiService.setMessage({
        objectId: 'freight-ship-pending',
        message: [successMessage],
        level: EMessageLevel.INFO,
      });

      // TODO review, after submit, clear forms and  this.freightId() = '';
      this.resetFormsAndData();
      this.freightShipService.refreshAfterSaveData();

      // TODO reset / remove freight messages ¿?

      // this.router.navigate(['/posted-freight']);
      this.ionicScrollManagerService.navigateAndScrollToTop('/posted-freight');
    } catch (error) {
      // TODO show error
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
      console.error(`Error al procesar el freight: ${errorMsg}`);
    }
  }

  // -- private

  private validateAllForms(): string[] {
    const errors = [];

    const generalForm = this.generalForm.first;
    if (!generalForm.validateForm()) {
      errors.push('datos generales');
    }

    this.syncAllStopForms();

    const stopForms = this.stopForms.toArray();
    stopForms.filter((stop, index) => {
      const isValid = stop.validateForm();
      if (!isValid) {
        errors.push(`parada #${index + 1} con errores`);
      }
    });

    return errors;
  }

  private checkFreightInStore(): void {
    if (this.freightShipFormsService.isFreightInStore(this.freightId())) {
      this.showAlertLoadFromStore();
    } else {
      this.loadFreightDetail();
    }
  }

  private async showAlertLoadFromStore(): Promise<void> {
    // TODO add freight shipper reference in message????
    const message = this.translate.instant('freight.post.load_from_store_msg', {});

    const alert = await this.alertController.create({
      header: this.translate.instant('general.attention'),
      message: new IonicSafeString(message),
      backdropDismiss: false,
      buttons: [
        {
          text: this.translate.instant('general.no'),
          role: 'cancel',
          handler: () => {
            this.loadFreightDetail();
          },
        },
        {
          text: this.translate.instant('freight.post.recover_changes'),
          role: 'confirm',
          handler: () => {
            this.freightShipFormsService.getFromStore(this.freightId());
          },
        },
      ],
    });

    await alert.present();
  }

  private loadFreightDetail(): void {
    if (this.freightId() !== UUID_NULL) {
      this.freightShipService.findFreightShipDetail$(this.freightId()).subscribe((freight) => {
        this.freightShipFormsService.setCurrentFreight(freight);
      });
    }
  }

  /* Necessary to prevent corner cases (e.g. tapping the stop order change button without the stop form losing focus,
  then the data would not have been updated; or also to force business rule validations when pressing the save button
  when the form has not yet been used) */
  private syncAllStopForms(): void {
    if (!this.stopForms) return;
    const stopFormComponents = this.stopForms.toArray();
    stopFormComponents.forEach((stopForm) => {
      const formValues = stopForm.formData;
      this.freightShipFormsService.updateStop(formValues.id, formValues);
    });
  }

  // TODO review this, maybe duplicate functionalities with service ???
  private resetFormsAndData(): void {
    this.freightShipFormsService.removeFromStore(this.freightId());
    this.freightShipFormsService.resetAll();
    this.generalForm.first.resetForm();
    // this.freightId.set(UUID_NULL);
  }

  private moveScroll(stopId: UUID | string, time: number): void {
    if (stopId) {
      setTimeout(() => {
        const element = document.getElementById(stopId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, time);
    }
  }
}
